import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Image } from "@/components/ui/image";

type AssetImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** CDN url (typically from a .asset.json pointer) */
  src: string;
  /** How many silent retries before showing the placeholder */
  maxRetries?: number;
  /** Extra classes applied to the fallback placeholder box */
  fallbackClassName?: string;
  /**
   * Locks the reserved space (e.g. "16 / 9", "1 / 1") so the skeleton and the
   * loaded image occupy identical boxes: no layout shift while the CDN responds.
   */
  aspect?: string;
  /** Classes for the aspect-locking wrapper (only used when `aspect` is set) */
  frameClassName?: string;
};

const RETRY_DELAYS_MS = [400, 1200];

/**
 * Image wrapper for CDN-hosted assets.
 * Shows a subtle skeleton while loading, silently retries transient CDN
 * failures with a cache-busted URL, and degrades to a branded placeholder
 * instead of a broken thumbnail.
 */
export function AssetImage({
  src,
  maxRetries = 2,
  className,
  fallbackClassName,
  aspect,
  frameClassName,
  alt = "",
  onLoad,
  onError,
  ...rest
}: AssetImageProps) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<"loading" | "loaded" | "failed">("loading");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setAttempt(0);
    setState("loading");
  }, [src]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const resolvedSrc = useMemo(
    () => (attempt === 0 ? src : `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}`),
    [src, attempt],
  );

  const handleError = useCallback<React.ReactEventHandler<HTMLImageElement>>(
    (event) => {
      onError?.(event);
      if (attempt < maxRetries) {
        const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
        timer.current = setTimeout(() => setAttempt((a) => a + 1), delay);
        return;
      }
      setState("failed");
    },
    [attempt, maxRetries, onError],
  );

  const withFrame = (node: React.ReactNode) =>
    aspect ? (
      <div
        className={cn("relative w-full overflow-hidden", frameClassName)}
        style={{ aspectRatio: aspect }}
      >
        {state === "loading" && (
          <div aria-hidden className="absolute inset-0 animate-pulse bg-muted/50" />
        )}
        {node}
      </div>
    ) : (
      node
    );

  if (state === "failed") {
    return withFrame(
      <div
        role="img"
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
        className={cn(
          "flex items-center justify-center rounded-md border border-border/60 bg-muted/40 text-muted-foreground",
          aspect && "absolute inset-0 h-full w-full",
          className,
          fallbackClassName,
        )}
      >
        <ImageOff className="h-5 w-5 opacity-60" aria-hidden />
      </div>,
    );
  }

  return withFrame(
    <Image
      {...rest}
      key={resolvedSrc}
      src={resolvedSrc}
      alt={alt}
      /* AssetImage owns the failure UI (retries, then the branded tile), so the
         wrapper's own fallback stays out of the way. */
      fallback={null}
      className={cn(
        aspect
          ? cn("absolute inset-0 h-full w-full", state === "loading" && "opacity-0")
          : state === "loading" && "animate-pulse bg-muted/40",
        className,
      )}
      onLoad={(event) => {
        setState("loaded");
        onLoad?.(event);
      }}
      onError={handleError}
    />,
  );
}

export default AssetImage;
